
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2Icon } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

interface PlotHousesProps {
  plotId: string;
}

export const PlotHouses = ({ plotId }: PlotHousesProps) => {
  const { toast } = useToast();
  
  const { data: houses = [], isLoading } = useQuery({
    queryKey: ["plot-houses", plotId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('houses')
        .select('id, name, address')
        .eq('plot_id', plotId);
      
      if (error) {
        console.error('Error fetching houses in plot:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las casas de la parcela",
          variant: "destructive",
        });
        return [];
      }
      
      return data || [];
    },
    enabled: !!plotId,
  });
  
  if (isLoading) {
    return (
      <Card className="w-full h-full">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Building2Icon className="mr-2 h-5 w-5" />
            Casas en la Parcela
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Building2Icon className="mr-2 h-5 w-5" />
          Casas en la Parcela
        </CardTitle>
      </CardHeader>
      <CardContent>
        {houses.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">No hay casas registradas en esta parcela.</p>
        ) : (
          <ScrollArea className="max-h-[16rem]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Dirección</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {houses.map((house) => (
                  <TableRow key={house.id}>
                    <TableCell className="font-medium">{house.name}</TableCell>
                    <TableCell>{house.address || 'Sin dirección'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
